import { apiClient, getErrorMessage } from '@/lib/apiClient';
import { AbpPagedRequest, AbpPagedResponse } from '@/lib/gridDataSourceUtils';
import {
  IdentityUser,
  CreateIdentityUser,
  UpdateIdentityUser,
  IdentityRole,
  CreateIdentityRole,
  UpdateIdentityRole,
  PermissionGroup,
  UpdatePermissions,
  UserLookup,
} from '@/types/identity/Identity';

// --- User Service ---

class IdentityUserService {
  private readonly baseUrl = '/api/identity/users';

  async getList(params: AbpPagedRequest & { filter?: string }): Promise<AbpPagedResponse<IdentityUser>> {
    try {
      const response = await apiClient.get<AbpPagedResponse<IdentityUser>>(this.baseUrl, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async getById(id: string): Promise<IdentityUser> {
    try {
      const response = await apiClient.get<IdentityUser>(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async create(data: CreateIdentityUser): Promise<IdentityUser> {
    try {
      const response = await apiClient.post<IdentityUser>(this.baseUrl, data);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error; // Preserve original error for validation error extraction
    }
  }

  async update(id: string, data: UpdateIdentityUser): Promise<IdentityUser> {
    try {
      const response = await apiClient.put<IdentityUser>(`${this.baseUrl}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async getUserRoles(id: string): Promise<{ items: { name: string }[] }> {
    try {
      const response = await apiClient.get<{ items: { name: string }[] }>(`${this.baseUrl}/${id}/roles`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user roles:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async getAssignableRoles(): Promise<{ items: IdentityRole[] }> {
    try {
      const response = await apiClient.get<{ items: IdentityRole[] }>(`${this.baseUrl}/assignable-roles`);
      return response.data;
    } catch (error) {
      console.error('Error fetching assignable roles:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async search(filter: string, maxResultCount = 10): Promise<UserLookup[]> {
    try {
      // Use the app's own user-picker endpoint (open to any authenticated user), NOT ABP's
      // /api/identity/users/lookup/search which is Identity-management permission-gated and 403s
      // for ordinary users — that 403 is why the picker came back empty. See UserPickerController.
      const response = await apiClient.get<UserLookup[]>('/api/identity/user-picker/search', {
        params: { filter, maxResultCount },
      });
      return response.data ?? [];
    } catch (error) {
      console.error('Error searching users:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Candidates for "Run as user". Deliberately a different endpoint from
   * `search`: this one is gated on Nexo.Users.Impersonate alone, so a support
   * session — which holds that and nothing else (D14) — can reach it, while the
   * ordinary picker's Core.General.Access baseline stays where it is.
   */
  async searchRunAsCandidates(filter: string, maxResultCount = 20): Promise<UserLookup[]> {
    try {
      const response = await apiClient.get<UserLookup[]>('/api/identity/run-as/search', {
        params: { filter, maxResultCount },
      });
      return response.data ?? [];
    } catch (error) {
      console.error('Error searching run-as candidates:', error);
      throw new Error(getErrorMessage(error));
    }
  }
}

// --- Role Service ---

class IdentityRoleService {
  private readonly baseUrl = '/api/identity/roles';

  async getList(params?: AbpPagedRequest & { filter?: string }): Promise<AbpPagedResponse<IdentityRole>> {
    try {
      const response = await apiClient.get<AbpPagedResponse<IdentityRole>>(this.baseUrl, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async getAll(): Promise<IdentityRole[]> {
    try {
      const response = await apiClient.get<{ items: IdentityRole[] }>(`${this.baseUrl}/all`);
      const data = response.data;
      return Array.isArray(data) ? data : data.items ?? [];
    } catch (error) {
      console.error('Error fetching all roles:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async getById(id: string): Promise<IdentityRole> {
    try {
      const response = await apiClient.get<IdentityRole>(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching role:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async create(data: CreateIdentityRole): Promise<IdentityRole> {
    try {
      const response = await apiClient.post<IdentityRole>(this.baseUrl, data);
      return response.data;
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  }

  async update(id: string, data: UpdateIdentityRole): Promise<IdentityRole> {
    try {
      const response = await apiClient.put<IdentityRole>(`${this.baseUrl}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Error deleting role:', error);
      throw new Error(getErrorMessage(error));
    }
  }
}

// --- Permission Service ---

class PermissionService {
  private readonly baseUrl = '/api/permission-management/permissions';

  async getForProvider(providerName: string, providerKey: string): Promise<{ groups: PermissionGroup[] }> {
    try {
      const response = await apiClient.get<{ groups: PermissionGroup[] }>(this.baseUrl, {
        params: { providerName, providerKey },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching permissions:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  async updateForProvider(providerName: string, providerKey: string, data: UpdatePermissions): Promise<void> {
    try {
      await apiClient.put(this.baseUrl, data, {
        params: { providerName, providerKey },
      });
    } catch (error) {
      console.error('Error updating permissions:', error);
      throw error;
    }
  }

  /** Get permissions for a role by name */
  async getRolePermissions(roleName: string) {
    return this.getForProvider('R', roleName);
  }

  /** Update permissions for a role by name */
  async updateRolePermissions(roleName: string, data: UpdatePermissions) {
    return this.updateForProvider('R', roleName, data);
  }

  /** Get permissions for a user by ID */
  async getUserPermissions(userId: string) {
    return this.getForProvider('U', userId);
  }

  /** Update permissions for a user by ID */
  async updateUserPermissions(userId: string, data: UpdatePermissions) {
    return this.updateForProvider('U', userId, data);
  }
}

// --- Exports ---

export const identityUserService = new IdentityUserService();
export const identityRoleService = new IdentityRoleService();
export const permissionService = new PermissionService();
